import {
    Button,
    Cell,
    Loading,
    PageTitle,
    Picker,
    Row,
    Spacer,
    StyleFunction,
    Surface,
    Table,
    TextInput,
    Title,
    useThemedStyle,
    useToast,
} from "@venuepos/react-common";
import {
    GQAdyenTerminalsQuery,
    GQCashRegistersQuery,
    GQDepartmentsQuery,
    GQSoftpayCloudTerminalsQuery,
    useAdyenTerminalsQuery,
    useCashRegisterEditMutation,
    useCashRegistersQuery,
    useDepartmentsQuery,
    useEntityConfigSaveMutation,
    useEntityConfigsQuery,
    useSoftpayCloudTerminalsQuery,
} from "graphql-sdk";
import { AvailableLocale, useTranslation } from "locales";
import React, { useCallback, useEffect, useState } from "react";
import { StyleProp, View, ViewStyle } from "react-native";
import { useHandleMutationError } from "../../hooks";
import { RootStackScreenProps } from "../../navigation";
import { AdminContainer } from "../container";
import { formatDateTime } from "lib";
import { useAdminSession } from "../../session";

type ScreenProps = RootStackScreenProps<"CASH_REGISTERS_MULTI_EDIT">;

export function CashRegistersMultiEditScreen({}: ScreenProps) {
    const [t] = useTranslation();

    const styles = useThemedStyle(styleFunc);

    const [cashRegisterIds, setCashRegisterIds] = useState<string[]>([]);

    const { data: cashRegisterData, loading: cashRegisterLoading } =
        useCashRegistersQuery({
            variables: {
                pagination: {
                    page: 0,
                    pageSize: 9999,
                },
            },
            fetchPolicy: "no-cache",
        });

    const { data: departmentsData, loading: departmentsLoading } =
        useDepartmentsQuery({
            variables: {
                pagination: {
                    page: 0,
                    pageSize: 9999,
                },
            },
            fetchPolicy: "no-cache",
        });

    const { data: softpayData, loading: softpayLoading } =
        useSoftpayCloudTerminalsQuery({
            variables: {
                pagination: {
                    page: 0,
                    pageSize: 9999,
                },
            },
            fetchPolicy: "no-cache",
        });
    const { data: adyenData, loading: adyenLoading } = useAdyenTerminalsQuery({
        variables: {
            pagination: {
                page: 0,
                pageSize: 9999,
            },
        },
        fetchPolicy: "no-cache",
    });

    const { data: entityConfigData, loading: entityConfigLoading } =
        useEntityConfigsQuery({
            variables: { entityIds: cashRegisterIds },
            fetchPolicy: "no-cache",
        });

    useEffect(() => {
        if (cashRegisterLoading) {
            return;
        }
        if (cashRegisterData) {
            setCashRegisterIds(
                cashRegisterData.cashRegisters.data.map(itr => itr.id)
            );
        }
    }, [cashRegisterData, cashRegisterLoading]);

    if (
        cashRegisterLoading ||
        entityConfigLoading ||
        softpayLoading ||
        adyenLoading ||
        departmentsLoading
    ) {
        return (
            <>
                <Spacer space={1} />
                <Loading />
            </>
        );
    }

    return (
        <AdminContainer>
            <PageTitle>
                {t("backoffice.cash_register.multi_edit.titel", "Multi edit")}
            </PageTitle>
            <View style={styles.box}>
                <View style={styles.container}>
                    <Surface>
                        <Table>
                            <Row>
                                <Title numeric style={styles.numberCell} />
                                <Title style={styles.cashRegisterCell}>
                                    {t("backoffice.common.name", "Name")}
                                </Title>
                                <Title style={styles.departmentCell}>
                                    {t(
                                        "backoffice.common.department",
                                        "Department"
                                    )}
                                </Title>
                                <Title style={styles.softpayCell}>
                                    {t(
                                        "backoffice.cash_register.multi_edit.softpay_cloud_terminal_id",
                                        "Softpay Cloud terminal app id"
                                    )}
                                </Title>
                                <Title style={styles.adyenCell}>
                                    {t(
                                        "backoffice.cash_register.multi_edit.adyen_terminal_poi_id",
                                        "Adyen Terminal API POI ID"
                                    )}
                                </Title>
                                <Title style={styles.saveCell} />
                            </Row>
                            {cashRegisterData.cashRegisters.data.map(
                                (item, index) => {
                                    // EntityData is saved as JSON stringified
                                    const entityData = JSON.parse(
                                        entityConfigData.entityConfigs.find(
                                            itr =>
                                                itr?.entityId &&
                                                itr.entityId === item.id
                                        )?.data || "{}"
                                    );
                                    return (
                                        <CashRegisterRow
                                            index={index}
                                            item={item}
                                            entityData={entityData}
                                            softpayData={
                                                softpayData
                                                    .softpayCloudTerminals.data
                                            }
                                            adyenData={
                                                adyenData.adyenTerminals.data
                                            }
                                            departments={
                                                departmentsData.departments.data
                                            }
                                            key={item.id}
                                            rowStyle={
                                                index % 2
                                                    ? styles.oddRow
                                                    : undefined
                                            }
                                        />
                                    );
                                }
                            )}
                        </Table>
                    </Surface>
                </View>
            </View>
        </AdminContainer>
    );
}

function CashRegisterRow({
    index,
    item,
    entityData,
    softpayData,
    adyenData,
    departments,
    rowStyle,
}: {
    index: number;
    item: GQCashRegistersQuery["cashRegisters"]["data"][0];
    entityData: {
        softpayCloudTerminalAppId?: string;
        adyenTerminalApiPoiId?: string;
    };
    softpayData: GQSoftpayCloudTerminalsQuery["softpayCloudTerminals"]["data"];
    adyenData: GQAdyenTerminalsQuery["adyenTerminals"]["data"];
    departments: GQDepartmentsQuery["departments"]["data"];
    rowStyle?: StyleProp<ViewStyle>;
}) {
    const [t] = useTranslation();
    const [{ locale }] = useAdminSession(["locale"]);
    const styles = useThemedStyle(styleFunc);
    const toast = useToast();

    const [entityConfigSave] = useEntityConfigSaveMutation();
    const [cashRegisterEdit] = useCashRegisterEditMutation();
    const { handleMutationError } = useHandleMutationError();

    const [cashRegisterNameValue, setCashRegisterNameValue] = useState(
        item?.name
    );
    const [departmentIdValue, setDepartmentIdValue] = useState(
        item?.department?.id
    );
    const [softpayCloudTerminalAppIdValue, setSoftpayCloudTerminalAppIdValue] =
        useState(entityData?.softpayCloudTerminalAppId);

    const [adyenTerminalApiPoiIdValue, setAdyenTerminalApiPoiIdValue] =
        useState(entityData?.adyenTerminalApiPoiId);

    const [hasChanges, setHasChanges] = useState(false);

    useEffect(() => {
        if (
            softpayCloudTerminalAppIdValue !==
                entityData?.softpayCloudTerminalAppId ||
            adyenTerminalApiPoiIdValue !== entityData?.adyenTerminalApiPoiId ||
            departmentIdValue !== item?.department?.id ||
            cashRegisterNameValue !== item.name
        ) {
            setHasChanges(true);
        } else {
            setHasChanges(false);
        }
    }, [
        adyenTerminalApiPoiIdValue,
        cashRegisterNameValue,
        departmentIdValue,
        entityData?.adyenTerminalApiPoiId,
        entityData?.softpayCloudTerminalAppId,
        item?.department?.id,
        item.name,
        softpayCloudTerminalAppIdValue,
    ]);

    const handleSubmit = useCallback(async () => {
        const disabledData = [];

        const partialData = {
            softpayCloudTerminalAppId: softpayCloudTerminalAppIdValue,
            adyenTerminalApiPoiId: undefined,
        };

        if (
            adyenTerminalApiPoiIdValue === "" ||
            adyenTerminalApiPoiIdValue === undefined
        ) {
            disabledData.push("adyenTerminalApiPoiId");
        } else {
            partialData.adyenTerminalApiPoiId = adyenTerminalApiPoiIdValue;
        }

        if (softpayCloudTerminalAppIdValue === "") {
            disabledData.push("softpayCloudTerminalAppId");
        } else {
            partialData.softpayCloudTerminalAppId =
                softpayCloudTerminalAppIdValue;
        }

        if (
            softpayCloudTerminalAppIdValue !==
                entityData?.softpayCloudTerminalAppId ||
            adyenTerminalApiPoiIdValue !== entityData?.adyenTerminalApiPoiId
        ) {
            await handleMutationError(
                async () =>
                    await entityConfigSave({
                        variables: {
                            entityId: item.id,
                            partialData: JSON.stringify(partialData),
                            disabledData: disabledData,
                        },
                    })
            );

            // Update the base values to enable us to detect changes
            entityData.softpayCloudTerminalAppId =
                softpayCloudTerminalAppIdValue;
            entityData.adyenTerminalApiPoiId = adyenTerminalApiPoiIdValue;
        }

        if (
            departmentIdValue !== item?.department?.id ||
            cashRegisterNameValue !== item.name
        ) {
            await handleMutationError(
                async () =>
                    await cashRegisterEdit({
                        variables: {
                            id: item.id,
                            cashRegister: {
                                name: cashRegisterNameValue,
                                departmentId:
                                    departmentIdValue === ""
                                        ? undefined
                                        : departmentIdValue,
                            },
                        },
                    })
            );

            // Update the base values to enable us to detect changes
            item.name = cashRegisterNameValue;
            if (departmentIdValue === undefined) {
                item.department = undefined;
            } else {
                if (
                    item.department === undefined ||
                    departmentIdValue === null
                ) {
                    item.department = { id: departmentIdValue, name: "" };
                }
                item.department.id = departmentIdValue;
            }
        }

        toast.success(t("common.saved", "Saved"));
    }, [
        softpayCloudTerminalAppIdValue,
        adyenTerminalApiPoiIdValue,
        entityData,
        departmentIdValue,
        item,
        cashRegisterNameValue,
        toast,
        t,
        handleMutationError,
        entityConfigSave,
        cashRegisterEdit,
    ]);

    return (
        <Row style={rowStyle}>
            <Cell numeric style={styles.numberCell}>
                {index + 1}
            </Cell>
            <Cell style={styles.cashRegisterCell}>
                <TextInput
                    value={cashRegisterNameValue}
                    onChangeText={setCashRegisterNameValue}
                    style={styles.nameInput}
                />
            </Cell>
            <Cell style={styles.departmentCell}>
                <Picker
                    selectedValue={departmentIdValue}
                    onValueChange={setDepartmentIdValue}
                >
                    <Picker.Item
                        label={t(
                            "backoffice.cash_register.multi_edit.no_department",
                            "No department"
                        )}
                        value=""
                    />
                    {departments.map(itr => (
                        <Picker.Item
                            value={itr.id}
                            label={itr.name}
                            key={item.id + itr.id}
                        />
                    ))}
                </Picker>
            </Cell>
            <Cell style={styles.softpayCell}>
                <Picker
                    selectedValue={softpayCloudTerminalAppIdValue}
                    onValueChange={val => {
                        setSoftpayCloudTerminalAppIdValue(val.trim());
                    }}
                >
                    <Picker.Item
                        label={t(
                            "backoffice.cash_register.multi_edit.no_terminal",
                            "No terminal"
                        )}
                        value=""
                    />
                    {softpayData.map(itr => (
                        <Picker.Item
                            value={itr.appId}
                            label={itr.name}
                            key={item.id + itr.appId}
                        />
                    ))}
                </Picker>
            </Cell>
            <Cell style={styles.adyenCell}>
                <Picker
                    selectedValue={adyenTerminalApiPoiIdValue}
                    onValueChange={val => {
                        setAdyenTerminalApiPoiIdValue(val.trim());
                    }}
                >
                    <Picker.Item
                        label={t(
                            "backoffice.cash_register.multi_edit.no_terminal",
                            "No terminal"
                        )}
                        value={""}
                    />
                    {adyenData.map(itr => {
                        const lastActivityAt = itr.lastActivityAt
                            ? ` ${t(
                                  "backoffice.cash_register.multi_edit.last_activity",
                                  "last activity"
                              )}: ` +
                              formatDateTime(
                                  new Date(parseInt(itr.lastActivityAt, 10)),
                                  locale as AvailableLocale
                              )
                            : "";
                        const lastTransactionAt = itr.lastTransactionAt
                            ? `, ${t(
                                  "backoffice.cash_register.multi_edit.last_transaction",
                                  "last transaction"
                              )}: ` +
                              formatDateTime(
                                  new Date(parseInt(itr.lastTransactionAt, 10)),
                                  locale as AvailableLocale
                              )
                            : "";
                        return (
                            <Picker.Item
                                value={itr.id}
                                label={`${itr.id}${lastActivityAt}${lastTransactionAt}`}
                                key={item.id + itr.id}
                            />
                        );
                    })}
                </Picker>
            </Cell>
            <Cell style={styles.saveCell}>
                <Button
                    size="small"
                    onPress={handleSubmit}
                    disabled={!hasChanges}
                    variant={hasChanges ? "outline" : "outline"}
                >
                    {t("common.save", "Save")}
                </Button>
            </Cell>
        </Row>
    );
}

const styleFunc: StyleFunction = theme => ({
    container: {
        justifyContent: "flex-end",
        flexWrap: "wrap",
    },
    oddRow: {
        ...theme.styles.oddRow,
    },

    nameInput: {
        ...theme.styles.flex1,
        minWidth: 0,
    },

    numberCell: {
        flexBasis: theme.spacingScale * 5,
        flexGrow: 0,
        flexShrink: 0,
        paddingHorizontal: theme.spacingScale / 2,
    },

    cashRegisterCell: {
        flex: 1,
        paddingTop: theme.spacingScale,
        paddingHorizontal: theme.spacingScale / 2,
    },
    departmentCell: {
        flex: 1,
        paddingTop: theme.spacingScale,
        paddingHorizontal: theme.spacingScale / 2,
    },
    softpayCell: {
        flex: 1,
        paddingTop: theme.spacingScale,
        paddingHorizontal: theme.spacingScale / 2,
    },
    adyenCell: {
        flex: 1,
        paddingTop: theme.spacingScale,

        paddingHorizontal: theme.spacingScale / 2,
    },
    saveCell: {
        flexBasis: theme.spacingScale * 15,
        flexGrow: 0,
        flexShrink: 0,

        paddingHorizontal: theme.spacingScale / 2,
    },
});
